// src/i18n/speechLocale.ts
// -----------------------------------------------------------------------------
// Maps the app's UI language to Azure Speech BCP-47 locales and builds the
// candidate list for fast-transcription language identification:
//   the doctor's CURRENT UI language FIRST, then the most common languages,
//   deduped by base language (LID is per-language, not per-dialect) and capped
//   at 10 (Azure's continuous-LID limit). See cursor_logs/035 Part B / B2.
// -----------------------------------------------------------------------------

/** App i18n language code -> Azure Speech BCP-47 locale. */
const UI_TO_BCP47: Record<string, string> = {
  en: "en-US",
  es: "es-ES",
  ca: "ca-ES",
  de: "de-DE",
  fr: "fr-FR",
  it: "it-IT",
  pt: "pt-PT",
  nl: "nl-NL",
  pl: "pl-PL",
  tr: "tr-TR",
  ru: "ru-RU",
  ar: "ar-EG",
  hi: "hi-IN",
  ja: "ja-JP",
  ko: "ko-KR",
  zh: "zh-CN",
  eu: "eu-ES",
};

// The 9 most common languages worldwide. The doctor's UI language is prepended,
// so the final candidate list is <= 10 (Azure's continuous-LID cap).
const COMMON_LOCALES: string[] = [
  "en-US",
  "es-ES",
  "zh-CN",
  "hi-IN",
  "ar-EG",
  "fr-FR",
  "pt-PT",
  "ru-RU",
  "de-DE",
];

/** Single best-guess locale for a UI language (fallback: Spanish). */
export function speechLocaleFor(uiLang: string): string {
  return UI_TO_BCP47[uiLang] || "es-ES";
}

/**
 * Warn once per browser session when a transcript's detected language is not
 * English, since STT quality is highest in English. Non-blocking and gentle —
 * the clinician still reviews before saving.
 */
export function warnIfNonEnglish(
  language: string | null | undefined,
  notify: { warning: (msg: string) => void },
  t: (key: string, fallback: string) => string
): void {
  if (!language || language.toLowerCase().startsWith("en")) return;
  try {
    if (sessionStorage.getItem("voiceLangWarned")) return;
    sessionStorage.setItem("voiceLangWarned", "1");
  } catch {
    /* sessionStorage unavailable — fall through and warn */
  }
  notify.warning(
    t(
      "chat.transcription_lang_note",
      "Voice transcription is most accurate in English. Please review non-English transcripts carefully before saving."
    )
  );
}

/**
 * Candidate locales for language identification: the doctor's UI language first,
 * then the most common ones, deduped by base language and capped at 10.
 */
export function speechLocalesFor(uiLang: string): string[] {
  const out: string[] = [];
  const seenBase = new Set<string>();
  const add = (loc?: string) => {
    if (!loc) return;
    const base = loc.split("-")[0];
    if (seenBase.has(base)) return;
    seenBase.add(base);
    out.push(loc);
  };
  add(UI_TO_BCP47[uiLang]); // doctor's current UI language takes priority
  COMMON_LOCALES.forEach(add);
  return out.slice(0, 10);
}
